import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, XAxis, YAxis, ReferenceLine, ResponsiveContainer, Tooltip, Cell,
} from 'recharts';
import { ChevronDown, Activity, Loader2, AlertTriangle } from 'lucide-react';
import { useEngineAnalysis } from '@/hooks/useEngineAnalysis';
import { trackProductEvent } from '@/lib/analytics';

const INCREASE = 'var(--carbon-800)';
const DECREASE = 'var(--green)';

/** Formats a sensitivity point value for display. */
function usePointFormatter(formatCost) {
  const { t } = useTranslation();
  return (variable, value) => {
    switch (variable.valueType) {
      case 'currency': return formatCost(value);
      case 'level': return `${t('methodologyPage.level')} ${value}`;
      case 'infra': return t(`calcData.infra.${value}`);
      case 'approach': return t(`approach.${value}`);
      case 'reuse':
        if (typeof value === 'number') return value > 0 ? `−${value}%` : '0%';
        return value === 'none' ? t('sensitivity.reuseNone') : t('sensitivity.reuseMax');
      default: return String(value);
    }
  };
}

/**
 * Cost sensitivity — ranked, one-variable-at-a-time modelled impact.
 * Every point is a full server-side engine recalculation, never a multiplied
 * isolated delta.
 */
export function SensitivityPanel({ config, formatCost, defaultOpen = false }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const { data, loading, error, retry } = useEngineAnalysis(
    '/estimates/sensitivity', { config }, { enabled: open, delay: 500 },
  );
  const formatPoint = usePointFormatter(formatCost);
  const userOpened = useRef(false);
  const engaged = useRef(false);

  // Engagement = the user opened the panel AND authoritative swings rendered.
  // Mounting, becoming visible, rerenders and failed loads never count, and it
  // is recorded at most once per calculator mount.
  useEffect(() => {
    if (engaged.current || !userOpened.current || !open || loading || error) return;
    if (!data?.variables?.length) return;
    engaged.current = true;
    trackProductEvent('sensitivity_engaged', { source: 'calculator', interaction: 'panel_opened' });
  }, [open, loading, error, data]);

  const variables = data?.variables || [];
  const max = Math.max(1, data?.maxAbsCostDelta || 1);
  const chartData = variables.map(v => ({
    id: v.id,
    label: t(`sensitivity.vars.${v.id}`),
    low: v.lowCostDelta,
    high: v.highCostDelta,
  }));

  const shortCost = (value) => {
    const abs = Math.abs(value);
    if (abs >= 1000) return `${value < 0 ? '−' : ''}${Math.round(abs / 1000)}k`;
    return `${value < 0 ? '−' : ''}${abs}`;
  };

  return (
    <section className="border border-[#DDE1E5] rounded-lg bg-white" data-testid="sensitivity-panel">
      <button
        type="button"
        onClick={() => setOpen(o => { if (!o) userOpened.current = true; return !o; })}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-[#F5F6F7] transition-colors duration-150 rounded-lg"
        data-testid="sensitivity-toggle"
      >
        <span className="min-w-0 flex items-start gap-2.5">
          <Activity className="w-4 h-4 text-[#66707A] mt-0.5 shrink-0" aria-hidden="true" />
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-[#171A1D]">{t('sensitivity.title')}</span>
            <span className="block mt-0.5 text-xs text-[#66707A]">{t('sensitivity.subtitle')}</span>
          </span>
        </span>
        <ChevronDown
          className={`w-4 h-4 text-[#66707A] shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div className="border-t border-[#DDE1E5] px-5 py-4" data-testid="sensitivity-body">
          {loading && (
            <div className="flex items-center gap-2 py-6 text-sm text-[#66707A]" data-testid="sensitivity-loading">
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> {t('sensitivity.loading')}
            </div>
          )}

          {!loading && error && (
            <div className="py-6" data-testid="sensitivity-error">
              <p className="flex items-center gap-2 text-sm text-[#B42318]">
                <AlertTriangle className="w-4 h-4" aria-hidden="true" /> {t('sensitivity.error')}
              </p>
              <button
                type="button"
                onClick={retry}
                className="mt-2 text-xs font-medium text-[#527F19] hover:text-[#3F6114]"
                data-testid="sensitivity-retry"
              >
                {t('sensitivity.retry')}
              </button>
            </div>
          )}

          {!loading && !error && variables.length === 0 && (
            <p className="py-6 text-sm text-[#66707A]" data-testid="sensitivity-empty">{t('sensitivity.empty')}</p>
          )}

          {!loading && !error && variables.length > 0 && (
            <>
              <div style={{ height: chartData.length * 34 + 32 }} data-testid="sensitivity-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    layout="vertical"
                    margin={{ top: 4, right: 12, bottom: 4, left: 4 }}
                    barCategoryGap="24%"
                    aria-label={t('sensitivity.chartAria')}
                  >
                    <XAxis
                      type="number"
                      domain={[-max, max]}
                      tickFormatter={shortCost}
                      tick={{ fontSize: 10, fill: '#8A929B', fontFamily: 'IBM Plex Mono, monospace' }}
                      axisLine={{ stroke: '#DDE1E5' }}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="label"
                      width={132}
                      tick={{ fontSize: 11, fill: '#3A4249' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <ReferenceLine x={0} stroke="#171A1D" strokeWidth={1} />
                    <Tooltip
                      formatter={(value) => formatCost(value)}
                      contentStyle={{
                        borderRadius: 6, border: '1px solid #DDE1E5', fontSize: 12,
                        fontFamily: 'IBM Plex Sans, sans-serif',
                      }}
                    />
                    <Bar dataKey="low" stackId="delta" name={t('sensitivity.decrease')} radius={[2, 2, 2, 2]}>
                      {chartData.map(row => <Cell key={`low-${row.id}`} fill={DECREASE} />)}
                    </Bar>
                    <Bar dataKey="high" stackId="delta" name={t('sensitivity.increase')} radius={[2, 2, 2, 2]}>
                      {chartData.map(row => <Cell key={`high-${row.id}`} fill={INCREASE} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-1 flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] text-[#66707A]"
                   data-testid="sensitivity-legend">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-3 h-2 rounded-sm" style={{ background: DECREASE }} aria-hidden="true" />
                  {t('sensitivity.decrease')}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-3 h-2 rounded-sm" style={{ background: INCREASE }} aria-hidden="true" />
                  {t('sensitivity.increase')}
                </span>
              </div>

              <dl x-excluded="true" className="mt-3 divide-y divide-[#DDE1E5] border-t border-[#DDE1E5]">
                {variables.map(variable => (
                  <div key={variable.id} className="py-2.5" data-testid={`sensitivity-row-${variable.id}`}>
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                      <dt className="text-sm text-[#3A4249]">
                        {t(`sensitivity.vars.${variable.id}`)}
                        <span className="ml-2 text-[10px] uppercase tracking-[0.08em] text-[#8A929B]"
                              style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                          {variable.swing}
                        </span>
                      </dt>
                      <dd className="text-xs text-[#66707A]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                        {t('sensitivity.baseline')}: {formatPoint(variable, variable.baselineValue)}
                      </dd>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1">
                      {variable.points.map(point => (
                        <span
                          key={`${variable.id}-${point.value}`}
                          className="text-xs text-[#3A4249]"
                          style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                        >
                          {formatPoint(variable, point.value)}
                          <span className={point.costDelta > 0 ? 'ml-1.5 text-[#171A1D]' : 'ml-1.5 text-[#527F19]'}>
                            {point.costDelta > 0 ? '+' : '−'}{formatCost(Math.abs(point.costDelta))}
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </dl>

              <p className="mt-3 pt-3 border-t border-[#DDE1E5] text-xs text-[#8A929B]"
                 data-testid="sensitivity-method-note">
                {t('sensitivity.method')}
              </p>
            </>
          )}
        </div>
      )}
    </section>
  );
}
